<template>
  <v-container>
    <DayButtons
      ref="dayButtons"
      v-on:today="load('today')"
      v-on:tomorrow="load('tomorrow')"
    ></DayButtons>

    <!-- The date the data itself carries, not the one the buttons imply. If the
         two ever disagree, this is what makes it visible to the reader. -->
    <p class="menu-date" v-if="dataDate">{{ dataDate }}</p>

    <!-- On a slow connection the switch between days is otherwise a blank gap
         with nothing to say a request is in flight. Reserves its own height so
         the page doesn't jump when the menus arrive. -->
    <div v-if="loading" class="loading-note" role="status">
      <v-progress-circular
        indeterminate
        color="buttonSelected"
        size="44"
        width="4"
      ></v-progress-circular>
      <p class="loading-text">Haetaan lounaita…</p>
    </div>

    <v-row v-else>
      <v-col
        cols="12"
        sm="6"
        md="4"
        class="mb-0 pb-0 pt-0 mt-0"
        v-for="(singleRestaurant, index) in FOODS"
        :key="index"
      >
        <!-- One table per restaurant, with <tr> rows inside a single <tbody>.
             This used to be a <table> nested directly inside v-simple-table's
             own <table>, with <th>/<td> as direct children of one <tbody> per
             dish and no <tr> at all. Vue builds that DOM itself, so the HTML
             parser never inserts the missing rows, and each engine was left to
             invent anonymous table boxes — some rendered only the first dish. -->
        <table class="lunch-table mb-4">
          <thead>
            <tr class="lunch-table-header">
              <th colspan="2">
                <p class="mb-0">
                  <a :href="singleRestaurant.URL">
                    {{ singleRestaurant.RESTAURANT }}
                  </a>
                </p>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(dish, dishIndex) in dishesOf(singleRestaurant)"
              :key="dishIndex"
            >
              <th class="w-33">Ruoka {{ dishIndex + 1 }}</th>
              <td class="w-66">{{ dish }}</td>
            </tr>
          </tbody>
        </table>
      </v-col>
    </v-row>
  </v-container>
</template>


<script>
import DayButtons from "./dayButtons";
import { describeIsoDate, helsinkiDay, isSameDay } from "../helsinkiDate";

const FILES = {
  today: "/foodstoday.json",
  tomorrow: "/foodstomorrow.json",
};

export default {
  name: "App",
  components: { DayButtons },
  data() {
    return {
      FOODS: [],
      selected: "today",
      loading: false,
    };
  },
  created() {
    // Monotonic token, deliberately outside data() so bumping it doesn't
    // re-render. Two quick taps used to race: whichever response landed last
    // won, which could leave Saturday's menus under the Perjantai button.
    this.requestId = 0;
  },
  computed: {
    dataIsoDate() {
      return this.FOODS.length ? this.FOODS[0].DATE : "";
    },
    dataDate() {
      return describeIsoDate(this.dataIsoDate);
    },
  },
  methods: {
    // Blank slots are dropped once, here, so "Ruoka N" numbering stays
    // contiguous and the label and the dish cell can never disagree about
    // whether to render. The old template tested dish.length > 1 separately on
    // the <th> and the <td>, which also silently dropped one-character dishes.
    dishesOf(restaurant) {
      const dishes = Array.isArray(restaurant.Dishes) ? restaurant.Dishes : [];
      return dishes
        .map((dish) => (dish == null ? "" : String(dish).trim()))
        .filter((dish) => dish.length > 0);
    },

    load(which, options) {
      const opts = options || {};
      const requestId = ++this.requestId;
      this.selected = which;

      if (!opts.silent) {
        // Clearing first is deliberate: leaving the previous day's menus on
        // screen under the new day's heading is the exact confusion this
        // component is being fixed for. A background refresh keeps them —
        // blanking the page every time the tab regains focus would be worse
        // than the wait it reports.
        this.FOODS = [];
        this.loading = true;
      }

      // The file names are fixed but their meaning rolls at Helsinki midnight,
      // so a day-old cached copy is indistinguishable from a fresh one. The
      // query string busts the browser's own cache only — the website module
      // sets query_string = false, so CloudFront strips it — and the browser is
      // exactly the cache that can hand back a previous day's copy.
      const url = FILES[which] + (opts.bustCache ? "?t=" + Date.now() : "");

      return fetch(url)
        .then((res) => res.json())
        .then((out) => {
          // A slower response for a day the reader has already moved away from
          // must never overwrite the day they are looking at now.
          if (requestId !== this.requestId) return;
          this.FOODS = Array.isArray(out) ? out : [];
          this.loading = false;
          this.verifyDate(which, opts);
        })
        .catch((err) => {
          if (requestId !== this.requestId) return;
          this.loading = false;
          // Rethrowing produced an unhandled rejection and left the previous
          // day's table on screen with no sign that anything had failed.
          console.error("Lunch data load failed:", url, err);
        });
    },

    // Last line of defence: if what came back is not the day we asked for, it
    // came from a cache we don't control. Retry once past it, in the same mode
    // so a background refresh stays silent.
    verifyDate(which, opts) {
      const expected = helsinkiDay(which === "tomorrow" ? 1 : 0);
      if (!this.dataIsoDate || isSameDay(this.dataIsoDate, expected)) return;

      console.warn(
        "Stale lunch data:",
        this.dataIsoDate,
        "expected",
        expected.toISOString().slice(0, 10)
      );
      if (!opts.bustCache) {
        this.load(which, { bustCache: true, silent: opts.silent });
      }
    },

    // A frozen or bfcached tab shows the day it was last painted with and never
    // refetches — which is how a Friday afternoon visitor was served Thursday's
    // menu while the file in S3 was correct all along.
    refresh() {
      const rolled = this.$refs.dayButtons
        ? this.$refs.dayButtons.refreshLabels()
        : false;
      // Silent: the reader is looking at menus that are probably still right,
      // so replace them only once the fresh ones are in hand.
      this.load(rolled ? "today" : this.selected, {
        bustCache: true,
        silent: true,
      });
    },
    onVisibilityChange() {
      if (document.visibilityState === "visible") this.refresh();
    },
    onPageShow(event) {
      // persisted is true only for a back/forward-cache restore, where no
      // script ran and nothing on screen was refetched.
      if (event.persisted) this.refresh();
    },
  },
  mounted() {
    this.load("today");
    document.addEventListener("visibilitychange", this.onVisibilityChange);
    window.addEventListener("pageshow", this.onPageShow);
  },
  beforeDestroy() {
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
    window.removeEventListener("pageshow", this.onPageShow);
  },
};
</script>



<style scoped>
p {
  font-size: 10px;
}
a {
  color: #0067a7 !important;
  text-decoration: none;
  font-size: 16px;
}
a:hover {
  color: #0c0c0c;
  text-decoration: underline;
}

/* The one place the reader is told which day is on screen, so it needs room to
   read as a heading rather than as a caption squeezed under the buttons. */
.menu-date {
  text-align: center;
  font-size: 1.05rem;
  font-weight: 600;
  letter-spacing: 0.2px;
  color: #3d3d3d;
  margin: 0.75rem 0 1.25rem 0;
}

.loading-note {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  /* Roughly a screenful of menus, so arriving data doesn't yank the page. */
  min-height: 240px;
}

.loading-text {
  font-size: 0.95rem;
  color: #666666;
  margin: 0.9rem 0 0 0;
}

.lunch-table-header {
  text-align: center;
  background-color: #e6e6e6;
}

thead {
  border-bottom-width: 2px;
  vertical-align: bottom;
  border-bottom: 2px solid #e9ecef;
  border: 1px solid #e9ecef;
  padding: 4px;
}

th {
  border-bottom-width: 2px;
  vertical-align: top;
  border-bottom: 2px solid #e9ecef;
  border: 1px solid #e9ecef;
  padding: 4px;
}

td {
  border: 1px solid #e9ecef;
  padding-left: 10px;
}

.lunch-table {
  width: 100%;
  max-width: 100%;
  background-color: transparent;
  border-collapse: collapse;
  border: 1px solid #e9ecef;
  display: table;
  white-space: normal;
  line-height: normal;
  font-weight: normal;
  font-size: medium;
  font-style: normal;
  text-align: start;
  border-spacing: 2px;
  font-variant: normal;
}

.w-66 {
  width: 66% !important;
}
.w-33 {
  width: 33% !important;
}
</style>
